// src/components/FieldEventPanel.jsx
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { apiFetch } from "../api/api.js";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

// ─── Токен ────────────────────────────────────────────────────────────────────
function ensureToken() {
  const t =
    mapboxgl.accessToken ||
    process.env.REACT_APP_MAPBOX_TOKEN ||
    "";
  if (t && !mapboxgl.accessToken) mapboxgl.accessToken = t;
  return !!mapboxgl.accessToken;
}

// ─── Константи ────────────────────────────────────────────────────────────────
const POINT_TYPES = [
  { value: "WEED",    label: "Бур'ян",  icon: "🌿", color: "#ef4444" },
  { value: "PEST",    label: "Шкідник", icon: "🐛", color: "#f97316" },
  { value: "DISEASE", label: "Хвороба", icon: "🦠", color: "#eab308" },
  { value: "OTHER",   label: "Інше",    icon: "📌", color: "#94a3b8" },
];
const FIELD_EVENT_TYPES = [
  { value: "NOTE",             label: "Нотатка",        icon: "📝" },
  { value: "PLANTING",         label: "Посів",          icon: "🌱" },
  { value: "SPRAYING",         label: "Обприскування",  icon: "💧" },
  { value: "FERTILIZING",      label: "Підживлення",    icon: "🧪" },
  { value: "CULTIVATION",      label: "Обробіток",      icon: "🚜" },
  { value: "PEST_DETECTED",    label: "Шкідник",        icon: "🐛" },
  { value: "DISEASE_DETECTED", label: "Хвороба",        icon: "🦠" },
  { value: "STATUS_CHANGE",    label: "Зміна статусу",  icon: "🔄" },
  { value: "HARVEST",          label: "Збір врожаю",    icon: "🌾" },
];
const RADIUS_OPTIONS = [5, 10, 20, 50, 100, 200, 500];
const MAX_PHOTOS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function getFieldEventTypeInfo(type) {
  return FIELD_EVENT_TYPES.find(t => t.value === type) ?? null;
}

function getTypeInfo(type) {
  return POINT_TYPES.find(t => t.value === type) ?? POINT_TYPES[3];
}

// ─── Геометрія ────────────────────────────────────────────────────────────────
function getFieldBbox(geoJson) {
  if (!geoJson) return null;
  let geo = geoJson.type === "Feature" ? geoJson.geometry : geoJson;
  let coords = [];
  if (geo?.type === "Polygon")      coords = geo.coordinates?.flat(1) ?? [];
  if (geo?.type === "MultiPolygon") coords = geo.coordinates?.flat(2) ?? [];
  if (!coords.length) return null;
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

function getFieldCenter(geoJson) {
  const bb = getFieldBbox(geoJson);
  return bb ? [(bb[0] + bb[2]) / 2, (bb[1] + bb[3]) / 2] : [31, 48];
}

// ─── Point-in-polygon (ray casting) + tolerance ───────────────────────────────
function _raycastRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

function isInsideField(lat, lng, fieldGeoJson, toleranceM = 100) {
  if (!fieldGeoJson) return true;
  const geo = fieldGeoJson.type === "Feature" ? fieldGeoJson.geometry : fieldGeoJson;
  if (!geo) return true;

  let outerRings = [];
  if (geo.type === "Polygon")      outerRings = [geo.coordinates[0]];
  if (geo.type === "MultiPolygon") outerRings = geo.coordinates.map(p => p[0]);
  if (!outerRings.length) return true;

  // Точно всередині полігону
  for (const ring of outerRings) {
    if (_raycastRing(lng, lat, ring)) return true;
  }

  // Поза полігоном — перевіряємо допуск відносно bbox
  const bb = getFieldBbox(fieldGeoJson);
  if (!bb) return true;
  const tol = toleranceM / 111320;
  return lng >= bb[0] - tol && lng <= bb[2] + tol &&
         lat >= bb[1] - tol && lat <= bb[3] + tol;
}

// ─── Коло-полігон для радіусу ─────────────────────────────────────────────────
function makeCirclePolygon(lat, lng, radiusM, segments = 64) {
  const latRad = lat * Math.PI / 180;
  const coords = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * 2 * Math.PI;
    coords.push([
      lng + (radiusM / (111320 * Math.cos(latRad))) * Math.sin(a),
      lat + (radiusM / 111320) * Math.cos(a),
    ]);
  }
  return { type: "Polygon", coordinates: [coords] };
}

function formatDate(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (isNaN(d)) return null;
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return null; }
}

function plural(n, one, few, many) {
  if (n % 10 === 1 && n % 100 !== 11) return one;
  if ([2,3,4].includes(n % 10) && ![12,13,14].includes(n % 100)) return few;
  return many;
}

function readAsDataUrl(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = rej;
    fr.onload = () => res(fr.result);
    fr.readAsDataURL(file);
  });
}

// ─── Toast ────────────────────────────────────────────────────────────────────
let _toastT;
function showToast(msg) {
  let el = document.getElementById("__ev_toast__");
  if (!el) {
    el = document.createElement("div");
    el.id = "__ev_toast__";
    el.style.cssText = [
      "position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(10px);",
      "background:var(--panel,#1a1a1a);color:var(--text,#fff);",
      "border:1px solid var(--border,#2a2a2a);",
      "padding:10px 20px;border-radius:12px;font-size:13px;font-weight:700;",
      "z-index:99999;opacity:0;transition:opacity .2s,transform .2s;",
      "box-shadow:0 6px 24px rgba(0,0,0,.4);white-space:nowrap;",
    ].join("");
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.style.opacity = "1";
  el.style.transform = "translateX(-50%) translateY(0)";
  clearTimeout(_toastT);
  _toastT = setTimeout(() => {
    el.style.opacity = "0";
    el.style.transform = "translateX(-50%) translateY(10px)";
  }, 2600);
}

// ─── ViewMap (огляд подій) ────────────────────────────────────────────────────
function ViewMap({ fieldGeoJson, previousGeoJson, events, selectedKey, onOpenPhotos, onSelectPoint, onNavigate }) {
  const ref            = useRef(null);
  const mapRef         = useRef(null);
  const markerElems    = useRef({}); // key → { el, color }
  const selectedKeyRef = useRef(selectedKey);
  const onNavigateRef  = useRef(onNavigate);
  useEffect(() => { selectedKeyRef.current = selectedKey; }, [selectedKey]);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  useEffect(() => {
    if (!ref.current || !fieldGeoJson) return;
    if (!ensureToken()) return;

    markerElems.current = {};
    const center = getFieldCenter(fieldGeoJson);
    const map = new mapboxgl.Map({
      container: ref.current,
      style: SATELLITE_STYLE,
      center, zoom: 13,
    });
    mapRef.current = map;
    const markers = [];

    map.on("load", () => {
      addTerrainLayer(map);

      // Попередній контур — помаранчевий пунктир
      if (previousGeoJson) {
        const prevData = previousGeoJson.type === "FeatureCollection"
          ? previousGeoJson
          : { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: previousGeoJson.type === "Feature" ? previousGeoJson.geometry : previousGeoJson }] };
        map.addSource("fv-prev", { type: "geojson", data: prevData });
        map.addLayer({ id: "fv-prev-fill", type: "fill", source: "fv-prev",
          paint: { "fill-color": "#f97316", "fill-opacity": 0.06 } });
        map.addLayer({ id: "fv-prev-line", type: "line", source: "fv-prev",
          paint: { "line-color": "#f97316", "line-width": 2, "line-dasharray": [5, 4] } });
      }

      map.addSource("fv", { type: "geojson", data: fieldGeoJson });
      map.addLayer({ id: "fv-fill", type: "fill",   source: "fv", paint: { "fill-color": "#22c55e", "fill-opacity": 0.12 } });
      map.addLayer({ id: "fv-line", type: "line",   source: "fv", paint: { "line-color": "#22c55e", "line-width": 2.5 } });

      // Кола радіусів
      const radiusFeatures = [];
      events.forEach(ev => {
        (ev.points || []).forEach(pt => {
          if (!pt.latitude || !pt.longitude || !pt.radius) return;
          const info = getTypeInfo(pt.pointType || ev.pointType);
          radiusFeatures.push({
            type: "Feature",
            properties: { color: info.color },
            geometry: makeCirclePolygon(pt.latitude, pt.longitude, pt.radius),
          });
        });
      });
      if (radiusFeatures.length) {
        map.addSource("ev-radii", { type: "geojson", data: { type: "FeatureCollection", features: radiusFeatures } });
        map.addLayer({ id: "ev-radii-fill", type: "fill", source: "ev-radii",
          paint: { "fill-color": ["get", "color"], "fill-opacity": 0.15 } });
        map.addLayer({ id: "ev-radii-line", type: "line", source: "ev-radii",
          paint: { "line-color": ["get", "color"], "line-width": 2, "line-dasharray": [4, 3] } });
      }

      // Маркери точок
      events.forEach(ev => {
        (ev.points || []).forEach((pt, i) => {
          if (!pt.latitude || !pt.longitude) return;
          const info = getTypeInfo(pt.pointType || ev.pointType);
          const ptKey = `${ev.id}_${pt.id ?? i}`;

          // el — зовнішня обгортка (Mapbox пише сюди translate3d, не чіпаємо)
          const el = document.createElement("div");
          el.style.cssText = "width:32px;height:32px;display:flex;align-items:center;justify-content:center;";

          // inner — візуальний маркер (вільно скейлимо)
          const inner = document.createElement("div");
          inner.textContent = info.icon;
          inner.style.cssText = `
            width:32px;height:32px;border-radius:50%;
            background:${info.color};border:2.5px solid #fff;
            display:flex;align-items:center;justify-content:center;
            font-size:14px;cursor:pointer;
            box-shadow:0 2px 8px rgba(0,0,0,.5);
            transition:box-shadow .2s,border .2s,transform .2s;
          `;
          el.appendChild(inner);
          markerElems.current[ptKey] = { el: inner, color: info.color };
          el.addEventListener("click", () => onSelectPoint?.(selectedKeyRef.current === ptKey ? null : ptKey));

          const photos = pt.photoUrls?.filter(Boolean) ?? [];
          const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${pt.latitude},${pt.longitude}`;

          const popupNode = document.createElement("div");
          popupNode.style.cssText = "padding:8px 10px;font-family:system-ui,sans-serif;";

          const nameEl = document.createElement("b");
          nameEl.style.fontSize = "13px";
          nameEl.textContent = ev.name;
          popupNode.appendChild(nameEl);
          popupNode.appendChild(document.createElement("br"));

          const typeEl = document.createElement("span");
          typeEl.style.cssText = `font-size:11px;color:${info.color};`;
          typeEl.textContent = `${info.icon} ${info.label}`;
          popupNode.appendChild(typeEl);

          if (pt.radius) {
            popupNode.appendChild(document.createElement("br"));
            const radEl = document.createElement("span");
            radEl.style.cssText = "font-size:11px;color:#888;";
            radEl.textContent = `🔘 ${pt.radius}м`;
            popupNode.appendChild(radEl);
          }

          if (pt.note) {
            popupNode.appendChild(document.createElement("br"));
            const noteEl = document.createElement("span");
            noteEl.style.cssText = "font-size:11px;color:#555;";
            noteEl.textContent = `📝 ${pt.note}`;
            popupNode.appendChild(noteEl);
          }

          popupNode.appendChild(document.createElement("br"));
          const coordEl = document.createElement("span");
          coordEl.style.cssText = "font-size:10px;color:#999;font-family:monospace;";
          coordEl.textContent = `${pt.latitude.toFixed(5)}, ${pt.longitude.toFixed(5)}`;
          popupNode.appendChild(coordEl);
          popupNode.appendChild(document.createElement("br"));

          const btnRow = document.createElement("div");
          btnRow.style.cssText = "display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;align-items:center;";

          const routeBtn = document.createElement("a");
          routeBtn.href = mapsUrl;
          routeBtn.target = "_blank";
          routeBtn.rel = "noopener noreferrer";
          routeBtn.style.cssText = `display:inline-flex;align-items:center;gap:5px;background:${info.color};color:#fff;padding:6px 12px;border-radius:8px;font-size:12px;font-weight:700;text-decoration:none;box-shadow:0 2px 6px rgba(0,0,0,.2);`;
          routeBtn.textContent = "🧭 Маршрут";
          btnRow.appendChild(routeBtn);

          const trackBtn = document.createElement("button");
          trackBtn.type = "button";
          trackBtn.style.cssText = "display:inline-flex;align-items:center;gap:5px;background:rgba(59,130,246,.1);border:1px solid rgba(59,130,246,.3);color:#2563eb;padding:6px 12px;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;";
          trackBtn.textContent = "📍 Відстежити";
          trackBtn.addEventListener("click", () => {
            onNavigateRef.current?.({
              lat: pt.latitude, lng: pt.longitude,
              label: ev.name, color: info.color,
              radius: pt.radius || null,
            });
          });
          btnRow.appendChild(trackBtn);

          if (photos.length && onOpenPhotos) {
            const photoBtn = document.createElement("button");
            photoBtn.type = "button";
            photoBtn.style.cssText = "display:inline-flex;align-items:center;gap:5px;background:rgba(0,0,0,.07);border:1px solid rgba(0,0,0,.12);color:#111;padding:6px 12px;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;";
            photoBtn.textContent = `📷 ${photos.length} ${photos.length === 1 ? "фото" : "фото"}`;
            photoBtn.addEventListener("click", () => onOpenPhotos(photos, 0));
            btnRow.appendChild(photoBtn);
          }

          popupNode.appendChild(btnRow);
          const popup = new mapboxgl.Popup({ offset: 16, maxWidth: "240px" }).setDOMContent(popupNode);
          markers.push(
            new mapboxgl.Marker({ element: el, anchor: "center" })
              .setLngLat([pt.longitude, pt.latitude])
              .setPopup(popup)
              .addTo(map)
          );
        });
      });

      // Розширюємо bounds щоб включити і контур поля, і всі маркери
      const bb = getFieldBbox(fieldGeoJson);
      let minLng = bb ? bb[0] : Infinity, minLat = bb ? bb[1] : Infinity;
      let maxLng = bb ? bb[2] : -Infinity, maxLat = bb ? bb[3] : -Infinity;
      events.forEach(ev => (ev.points || []).forEach(pt => {
        if (!pt.latitude || !pt.longitude) return;
        minLng = Math.min(minLng, pt.longitude); maxLng = Math.max(maxLng, pt.longitude);
        minLat = Math.min(minLat, pt.latitude);  maxLat = Math.max(maxLat, pt.latitude);
      }));
      if (isFinite(minLng)) {
        map.fitBounds([[minLng, minLat],[maxLng, maxLat]], { padding: 64, duration: 0, maxZoom: 16 });
      }
    });

    return () => {
      markers.forEach(m => { try { m.remove(); } catch {} });
      try { map.remove(); } catch {}
      mapRef.current = null;
      markerElems.current = {};
    };
  }, [fieldGeoJson, events]);

  // Підсвічуємо вибраний маркер без перестворення карти
  useEffect(() => {
    Object.entries(markerElems.current).forEach(([key, { el, color }]) => {
      if (key === selectedKey) {
        el.style.transform = `scale(1.45)`;
        el.style.boxShadow = `0 0 0 3px #fff, 0 0 0 6px ${color}, 0 6px 20px rgba(0,0,0,.6)`;
        el.style.border    = `3px solid #fff`;
      } else {
        el.style.transform = `scale(1)`;
        el.style.boxShadow = `0 2px 8px rgba(0,0,0,.5)`;
        el.style.border    = `2.5px solid #fff`;
      }
    });
  }, [selectedKey]);

  return (
    <div ref={ref} style={{
      height: 340, borderRadius: 14, overflow: "hidden",
      border: "1px solid var(--border,#e5e7eb)",
    }} />
  );
}

// ─── toFeatureCollection ──────────────────────────────────────────────────────
function toFeatureCollection(geo) {
  if (!geo) return null;
  if (geo.type === "FeatureCollection") return geo;
  if (geo.type === "Feature") return { type: "FeatureCollection", features: [geo] };
  return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: geo }] };
}

// ─── Оновлення кіл радіусів на карті ─────────────────────────────────────────
function applyRadiiToMap(map, draftPoints) {
  if (!map) return;
  const features = draftPoints
    .filter(p => p.hasRadius && p.radius > 0)
    .map(pt => ({
      type: "Feature",
      properties: { color: getTypeInfo(pt.pointType).color },
      geometry: makeCirclePolygon(pt.lat, pt.lng, pt.radius),
    }));

  const data = { type: "FeatureCollection", features };

  if (map.getSource("radii-src")) {
    map.getSource("radii-src").setData(data);
  } else {
    map.addSource("radii-src", { type: "geojson", data });
    map.addLayer({ id: "radii-fill", type: "fill", source: "radii-src",
      paint: { "fill-color": ["get", "color"], "fill-opacity": 0.15 } });
    map.addLayer({ id: "radii-line", type: "line", source: "radii-src",
      paint: { "line-color": ["get", "color"], "line-width": 2,
               "line-dasharray": [4, 3] } });
  }
}

// Поза компонентом — немає проблеми зі stale closure в map.on("load")
function applyFieldToMap(map, fieldGeoJson) {
  if (!map || !fieldGeoJson) return;
  const data = toFeatureCollection(fieldGeoJson);
  if (!data?.features?.length) return;

  try {
    if (map.getSource("field-src")) {
      map.getSource("field-src").setData(data);
    } else {
      map.addSource("field-src", { type: "geojson", data });
      map.addLayer({ id: "field-fill", type: "fill", source: "field-src",
        paint: { "fill-color": "rgba(34,197,94,0.22)" } });
      map.addLayer({ id: "field-line", type: "line", source: "field-src",
        paint: { "line-color": "#22c55e", "line-width": 3 } });
    }

    const geom = data.features[0]?.geometry;
    if (!geom) return;
    let coords = [];
    if (geom.type === "Polygon")      coords = geom.coordinates.flat(1);
    if (geom.type === "MultiPolygon") coords = geom.coordinates.flat(2);
    if (!coords.length) return;

    const lngs = coords.map(c => c[0]);
    const lats  = coords.map(c => c[1]);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 60, duration: 0 }
    );
  } catch (err) {
    console.warn("[EditMap] applyField:", err);
  }
}

// ─── EditMap ──────────────────────────────────────────────────────────────────
function EditMap({ fieldGeoJson, draftPoints, onAddPoint }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markersRef   = useRef([]);
  // Зберігає актуальний fieldGeoJson щоб map.on("load") бачив його
  const geoJsonRef   = useRef(fieldGeoJson);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => { geoJsonRef.current = fieldGeoJson; }, [fieldGeoJson]);

  // ── INIT (один раз) ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || !ensureToken()) return;
    let alive = true;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: SATELLITE_STYLE,
      center: [30.5234, 50.4501],
      zoom: 10,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      if (!alive) return;
      map.resize();
      applyFieldToMap(map, geoJsonRef.current); // ref завжди актуальний
      addTerrainLayer(map);
    });

    map.on("click", e => {
      if (!alive) return;
      const { lat, lng } = e.lngLat;
      if (!isInsideField(lat, lng, geoJsonRef.current, 100)) {
        showToast("⚠️ Точка за межами поля");
        return;
      }
      onAddPointRef.current({ lat, lng });
    });

    return () => {
      alive = false;
      markersRef.current.forEach(m => { try { m.remove(); } catch {} });
      markersRef.current = [];
      try { map.remove(); } catch {}
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── КОНТУР: якщо fieldGeoJson змінився після ініціалізації карти ─────────
  // Використовуємо isStyleLoaded() як у MapboxFieldView — без стейту "ready"
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fieldGeoJson) return;

    if (map.isStyleLoaded()) {
      applyFieldToMap(map, fieldGeoJson);
    }
    // Якщо стиль ще не завантажився — map.on("load") вище відпрацює з geoJsonRef
  }, [fieldGeoJson]);

  // onAddPoint зберігаємо в ref щоб click-хендлер у init-ефекті завжди актуальний
  const onAddPointRef = useRef(onAddPoint);
  useEffect(() => { onAddPointRef.current = onAddPoint; }, [onAddPoint]);

  // ── МАРКЕРИ + РАДІУСИ ────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(m => { try { m.remove(); } catch {} });
    markersRef.current = [];

    draftPoints.forEach((pt, i) => {
      const info = getTypeInfo(pt.pointType);
      const el = document.createElement("div");
      el.style.cssText = `
        width:30px;height:30px;border-radius:50%;
        background:${info.color};
        border:2.5px solid rgba(255,255,255,0.95);
        box-shadow:0 2px 10px rgba(0,0,0,0.5),0 0 0 1.5px ${info.color}88;
        font-size:12px;font-weight:900;color:#fff;
        line-height:25px;text-align:center;
        cursor:pointer;user-select:none;
      `;
      el.textContent = i + 1;

      markersRef.current.push(
        new mapboxgl.Marker({ element: el, anchor: "center" })
          .setLngLat([pt.lng, pt.lat])
          .addTo(map)
      );
    });

    // Кола радіусів — тільки якщо стиль карти вже завантажений
    if (map.isStyleLoaded()) {
      applyRadiiToMap(map, draftPoints);
    }
  }, [draftPoints]);

  function geolocate() {
    if (!navigator.geolocation) { showToast("⚠️ Геолокація не підтримується"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        onAddPointRef.current({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 16 });
        setGeoLoading(false);
      },
      () => { showToast("⚠️ Не вдалося отримати геолокацію"); setGeoLoading(false); }
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{ height: 340, borderRadius: 14, overflow: "hidden", border: "1px solid var(--border,#ddd)" }}
      />
      <button
        type="button"
        onClick={geolocate}
        disabled={geoLoading}
        style={{
          position: "absolute", bottom: 12, left: 12, zIndex: 10,
          background: "rgba(255,255,255,0.95)", color: "#111",
          border: "none", borderRadius: 9,
          padding: "7px 13px", fontSize: 12, fontWeight: 700,
          cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.25)",
          display: "flex", alignItems: "center", gap: 5,
          opacity: geoLoading ? 0.6 : 1,
        }}
      >
        {geoLoading ? "⏳" : "📍"} {geoLoading ? "Визначаю..." : "Моя позиція"}
      </button>
    </div>
  );
}

// ─── PhotoSection ─────────────────────────────────────────────────────────────
function PhotoSection({ photos, onChange }) {
  const inputRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [viewerIdx, setViewerIdx] = useState(null);

  async function handleFiles(files) {
    if (!files.length) return;
    setReading(true);
    try {
      const toAdd = [];
      for (const f of Array.from(files).slice(0, MAX_PHOTOS - photos.length)) {
        if (!f.type.startsWith("image/")) continue;
        if (f.size > MAX_FILE_SIZE) { showToast("⚠️ Файл надто великий (макс. 5 МБ)"); continue; }
        const url = await readAsDataUrl(f);
        toAdd.push({ id: `${f.name}-${f.lastModified}`, url });
      }
      if (toAdd.length) onChange([...photos, ...toAdd]);
    } finally { setReading(false); }
  }

  function remove(id) { onChange(photos.filter(p => p.id !== id)); }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--muted,#9ca3af)", textTransform: "uppercase", letterSpacing: ".04em" }}>
          Фото {photos.length}/{MAX_PHOTOS}
        </span>
        {photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={reading}
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 7,
              border: "1px solid var(--border,#e5e7eb)",
              background: "var(--panel,#fff)", color: "var(--text,#111)",
              cursor: "pointer", fontWeight: 700,
            }}
          >
            {reading ? "Читаю..." : "📷 Додати"}
          </button>
        )}
        <input
          ref={inputRef} type="file" accept="image/*" multiple
          style={{ display: "none" }}
          onChange={e => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {photos.length > 0 ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {photos.map((p, j) => (
            <div key={p.id} style={{ position: "relative" }}>
              <img
                src={p.url} alt=""
                onClick={() => setViewerIdx(j)}
                style={{ width: 72, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border,#e5e7eb)", display: "block", cursor: "zoom-in" }}
              />
              <button
                type="button"
                onClick={() => remove(p.id)}
                style={{
                  position: "absolute", top: -6, right: -6,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "#ef4444", border: "none", color: "#fff",
                  cursor: "pointer", fontSize: 10,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 900, lineHeight: 1,
                }}
              >✕</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          fontSize: 11, color: "var(--muted,#9ca3af)", padding: "8px 10px",
          border: "1px dashed var(--border,#e5e7eb)", borderRadius: 8, textAlign: "center",
        }}>
          Натисни «Додати» щоб прикріпити фото до точки
        </div>
      )}

      {viewerIdx !== null && (
        <PhotoViewer
          photos={photos.map(p => p.url)}
          startIndex={viewerIdx}
          onClose={() => setViewerIdx(null)}
        />
      )}
    </div>
  );
}

// ─── PointRow ─────────────────────────────────────────────────────────────────
const miniLblStyle = {
  fontSize: 10, fontWeight: 700, color: "var(--muted,#9ca3af)",
  textTransform: "uppercase", letterSpacing: ".04em",
};

function PointRow({ point, index, onChange, onRemove }) {
  const info = getTypeInfo(point.pointType);
  return (
    <div style={{
      border: "1px solid var(--border,#e5e7eb)",
      borderLeft: `3px solid ${info.color}`,
      borderRadius: 10, padding: "10px 12px",
      background: "var(--surface-soft,#f9fafb)",
    }}>
      {/* Шапка */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{
          background: info.color, color: "#fff", borderRadius: 20,
          padding: "2px 9px", fontSize: 11, fontWeight: 800,
        }}>
          {info.icon} #{index + 1}
        </span>
        <span style={{ fontSize: 11, color: "var(--muted,#9ca3af)", fontFamily: "monospace", flex: 1 }}>
          {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
        </span>
        <button
          type="button" onClick={() => onRemove(index)}
          style={{
            background: "transparent", color: "#ef4444",
            border: "1px solid #fca5a5", padding: "2px 8px",
            borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700,
          }}
        >✕</button>
      </div>

      {/* Тип проблеми */}
      <div style={{ marginBottom: 8 }}>
        <div style={miniLblStyle}>Тип проблеми</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
          {POINT_TYPES.map(t => (
            <button
              key={t.value} type="button"
              onClick={() => onChange(index, "pointType", t.value)}
              style={{
                padding: "5px 10px", borderRadius: 7,
                fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all .12s",
                border: point.pointType === t.value ? `2px solid ${t.color}` : "2px solid transparent",
                background: point.pointType === t.value ? `${t.color}18` : "var(--panel,#fff)",
                color: point.pointType === t.value ? t.color : "var(--muted,#6b7280)",
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Радіус */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={miniLblStyle}>Радіус:</div>
        <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12 }}>
          <input
            type="checkbox" checked={point.hasRadius}
            onChange={e => onChange(index, "hasRadius", e.target.checked)}
            style={{ accentColor: info.color }}
          />
          <span style={{ color: "var(--muted,#6b7280)" }}>
            {point.hasRadius ? "Є" : "Без радіуса"}
          </span>
        </label>
        {point.hasRadius && (
          <select
            value={point.radius}
            onChange={e => onChange(index, "radius", Number(e.target.value))}
            style={{
              padding: "4px 8px", borderRadius: 7, fontSize: 12,
              border: `1px solid ${info.color}55`,
              background: "var(--panel,#fff)", color: "var(--text,#111)", cursor: "pointer",
            }}
          >
            {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} м</option>)}
          </select>
        )}
      </div>

      {/* Нотатка */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <div style={miniLblStyle}>Нотатка:</div>
        <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12 }}>
          <input
            type="checkbox" checked={point.hasNote ?? false}
            onChange={e => onChange(index, "hasNote", e.target.checked)}
            style={{ accentColor: info.color }}
          />
          <span style={{ color: "var(--muted,#6b7280)" }}>
            {point.hasNote ? "Є" : "Без нотатки"}
          </span>
        </label>
        {point.hasNote && (
          <input
            type="text"
            value={point.note ?? ""}
            onChange={e => onChange(index, "note", e.target.value)}
            maxLength={40}
            placeholder="До 40 символів..."
            style={{
              flex: 1, padding: "4px 8px", borderRadius: 7, fontSize: 12,
              border: `1px solid ${info.color}55`,
              background: "var(--panel,#fff)", color: "var(--text,#111)",
              outline: "none",
            }}
          />
        )}
      </div>

      {/* Фото */}
      <div style={{ borderTop: "1px solid var(--border,#e5e7eb)", paddingTop: 8 }}>
        <PhotoSection
          photos={point.photos ?? []}
          onChange={photos => onChange(index, "photos", photos)}
        />
      </div>
    </div>
  );
}

// ─── EventCard ────────────────────────────────────────────────────────────────
function EventCard({ event, fieldGeoJson, onDelete, onDeletePoint, selectedPointKey, onSelectPoint, onOpenPhotos, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const [deletingPointId, setDeletingPointId] = useState(null);
  const points    = event.points || [];
  const typeSet   = [...new Set(points.map(p => p.pointType || event.pointType).filter(Boolean))];
  const fetInfo   = getFieldEventTypeInfo(event.fieldEventType);
  const hasOutside = fieldGeoJson && points.length > 0 &&
    points.some(p => p.latitude && p.longitude && !isInsideField(p.latitude, p.longitude, fieldGeoJson, 100));

  return (
    <div style={{
      border: "1px solid var(--border,#e5e7eb)", borderRadius: 14,
      background: "var(--panel,#fff)", overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,.06)",
    }}>
      {typeSet.length > 0 && (
        <div style={{ height: 3, display: "flex" }}>
          {typeSet.map(t => <div key={t} style={{ flex: 1, background: getTypeInfo(t).color }} />)}
        </div>
      )}
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 5 }}>
              {fetInfo && (
                <span style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700,
                  background: "rgba(99,102,241,.1)", border: "1px solid rgba(99,102,241,.3)", color: "#6366f1",
                }}>{fetInfo.icon} {fetInfo.label}</span>
              )}
              {typeSet.map(t => {
                const info = getTypeInfo(t);
                return (
                  <span key={t} style={{
                    fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700,
                    background: `${info.color}15`, border: `1px solid ${info.color}40`, color: info.color,
                  }}>{info.icon} {info.label}</span>
                );
              })}
              {points.length > 0 && (
                <span style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700,
                  background: "rgba(34,197,94,.1)", border: "1px solid rgba(34,197,94,.3)", color: "#16a34a",
                }}>
                  {points.length} {plural(points.length, "точка", "точки", "точок")}
                </span>
              )}
              {hasOutside && (
                <span title="Одна або більше точок знаходяться за межами поточного контуру поля" style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700,
                  background: "rgba(234,179,8,.12)", border: "1px solid rgba(234,179,8,.4)", color: "#b45309",
                }}>⚠️ Поза контуром</span>
              )}
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text,#111)" }}>{event.name}</div>
            {event.describe && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted,#6b7280)", lineHeight: 1.4 }}>
                {event.describe}
              </p>
            )}
            {formatDate(event.createdAt) && (
              <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)", marginTop: 4 }}>
                🗓 {formatDate(event.createdAt)}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => setExpanded(v => !v)}
              style={{
                background: "transparent", color: "var(--muted,#6b7280)",
                border: "1px solid var(--border,#e5e7eb)", padding: "10px 14px",
                borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 700,
                minHeight: 44, display: "flex", alignItems: "center",
              }}
            >{expanded ? "▲" : "▼ Деталі"}</button>
            <button
              onClick={onDelete}
              style={{
                background: "rgba(239,68,68,.06)", color: "#ef4444",
                border: "1px solid rgba(239,68,68,.2)", padding: "10px 12px",
                borderRadius: 10, cursor: "pointer", fontSize: 16,
                minHeight: 44, minWidth: 44, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >🗑️</button>
          </div>
        </div>

        {expanded && (
          <div style={{ marginTop: 10, borderTop: "1px solid var(--border,#e5e7eb)", paddingTop: 10 }}>
            {points.length === 0 && (
              <p style={{ fontSize: 12, color: "var(--muted,#9ca3af)", margin: 0 }}>Немає точок</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 6 }}>
              {points.map((pt, i) => {
                const info = getTypeInfo(pt.pointType || event.pointType);
                const photos = pt.photoUrls?.filter(Boolean) ?? [];
                const key = pt.id ?? i;
                const globalKey = `${event.id}_${key}`;
                const isDeleting = deletingPointId === (pt.id ?? i);
                const isSelected = selectedPointKey === globalKey;
                return (
                  <div
                    key={key}
                    onClick={() => onSelectPoint?.(isSelected ? null : globalKey)}
                    style={{
                      background: isSelected ? `${info.color}12` : "var(--surface-soft,#f9fafb)",
                      border: isSelected ? `1.5px solid ${info.color}` : `1px solid ${info.color}22`,
                      borderLeft: `3px solid ${info.color}`,
                      borderRadius: 8, padding: "8px 10px",
                      opacity: isDeleting ? 0.5 : 1,
                      cursor: "pointer",
                      boxShadow: isSelected ? `0 0 0 3px ${info.color}22` : "none",
                      transition: "all .15s",
                    }}
                  >
                    <div style={{ display: "flex", gap: 7, marginBottom: photos.length ? 8 : 0 }}>
                      <span style={{ fontSize: 16 }}>{info.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)" }}>
                          #{i + 1} · <span style={{ color: info.color }}>{info.label}</span>
                        </div>
                        <div style={{ fontSize: 10, fontFamily: "monospace", color: "var(--muted,#9ca3af)" }}>
                          {pt.latitude?.toFixed(5)}, {pt.longitude?.toFixed(5)}
                        </div>
                        {pt.radius ? <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)" }}>🔘 {pt.radius}м</div> : null}
                        {pt.note  ? <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)" }}>📝 {pt.note}</div>  : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
                        {pt.latitude && pt.longitude && onNavigate && (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              onNavigate({ lat: pt.latitude, lng: pt.longitude, label: event.name, color: info.color, radius: pt.radius || null });
                            }}
                            style={{
                              background: "rgba(59,130,246,.1)", color: "#2563eb",
                              border: "1px solid rgba(59,130,246,.3)", cursor: "pointer",
                              fontSize: 11, padding: "2px 6px", borderRadius: 6, fontWeight: 700, lineHeight: 1.4,
                            }}
                            title="Відстежити на карті"
                          >🧭</button>
                        )}
                        {pt.id && onDeletePoint && (
                          <button
                            type="button"
                            disabled={isDeleting}
                            onClick={e => {
                              e.stopPropagation();
                              setDeletingPointId(pt.id ?? i);
                              onDeletePoint(event.id, pt.id).finally(() => setDeletingPointId(null));
                            }}
                            style={{
                              background: "rgba(239,68,68,.08)", color: "#ef4444",
                              border: "1px solid rgba(239,68,68,.2)", cursor: "pointer",
                              fontSize: 14, padding: "8px 10px", lineHeight: 1,
                              borderRadius: 8, minHeight: 36, minWidth: 36,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: isDeleting ? 0.4 : 1,
                            }}
                            title="Видалити точку"
                          >✕</button>
                        )}
                      </div>
                    </div>
                    {photos.length > 0 && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {photos.map((url, j) => (
                          <img
                            key={j} src={url} alt=""
                            onClick={e => { e.stopPropagation(); onOpenPhotos?.(photos, j); }}
                            style={{ width: 56, height: 44, objectFit: "cover", borderRadius: 6, border: `1px solid ${info.color}33`, cursor: "zoom-in" }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NavigateMap ─────────────────────────────────────────────────────────────
function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtDist(m) {
  if (m < 1000) return `${Math.round(m)} м`;
  return `${(m / 1000).toFixed(2)} км`;
}

function NavigateMap({ fieldGeoJson, target, onClose }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const posMarkerRef = useRef(null);
  const watchIdRef   = useRef(null);
  const userPosRef   = useRef(null);
  const [userPos, setUserPos] = useState(null);
  const [posErr,  setPosErr]  = useState(false);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || !ensureToken()) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: SATELLITE_STYLE,
      center: [target.lng, target.lat],
      zoom: 15,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      addTerrainLayer(map);

      // Field boundary
      if (fieldGeoJson) {
        const data = toFeatureCollection(fieldGeoJson);
        if (data) {
          map.addSource("nm-field", { type: "geojson", data });
          map.addLayer({ id: "nm-field-fill", type: "fill", source: "nm-field",
            paint: { "fill-color": "#22c55e", "fill-opacity": 0.12 } });
          map.addLayer({ id: "nm-field-line", type: "line", source: "nm-field",
            paint: { "line-color": "#22c55e", "line-width": 2.5 } });
        }
      }

      // Route line (empty until GPS fires)
      map.addSource("nm-line", {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } },
      });
      map.addLayer({ id: "nm-line-layer", type: "line", source: "nm-line",
        paint: {
          "line-color": "#3b82f6",
          "line-width": 2.5,
          "line-dasharray": [6, 4],
          "line-opacity": 0.75,
        },
      });

      // Target pin
      const pinEl = document.createElement("div");
      pinEl.style.cssText = `
        width:20px;height:20px;border-radius:50%;
        background:${target.color || "#ef4444"};
        border:3px solid #fff;
        box-shadow:0 0 0 4px ${target.color || "#ef4444"}44,0 3px 12px rgba(0,0,0,.45);
      `;
      new mapboxgl.Marker({ element: pinEl, anchor: "center" })
        .setLngLat([target.lng, target.lat])
        .addTo(map);

      // Outer ring marker (always visible at any zoom)
      const ringEl = document.createElement("div");
      ringEl.style.cssText = `
        width:44px;height:44px;border-radius:50%;
        border:2px solid ${target.color || "#ef4444"};
        background:${target.color || "#ef4444"}18;
        pointer-events:none;
      `;
      new mapboxgl.Marker({ element: ringEl, anchor: "center" })
        .setLngLat([target.lng, target.lat])
        .addTo(map);

      // Radius circle — same as shown in ViewMap
      if (target.radius && target.radius > 0) {
        const radiusGeo = makeCirclePolygon(target.lat, target.lng, target.radius);
        map.addSource("nm-radius", { type: "geojson", data: { type: "Feature", geometry: radiusGeo } });
        map.addLayer({ id: "nm-radius-fill", type: "fill", source: "nm-radius",
          paint: { "fill-color": target.color || "#ef4444", "fill-opacity": 0.10 } });
        map.addLayer({ id: "nm-radius-line", type: "line", source: "nm-radius",
          paint: {
            "line-color": target.color || "#ef4444",
            "line-width": 2,
            "line-dasharray": [5, 3],
            "line-opacity": 0.85,
          },
        });
      }

      // Fit to field + target
      const bb = getFieldBbox(fieldGeoJson);
      const tPad = 0.002;
      const bounds = bb
        ? [[Math.min(bb[0], target.lng - tPad), Math.min(bb[1], target.lat - tPad)],
           [Math.max(bb[2], target.lng + tPad), Math.max(bb[3], target.lat + tPad)]]
        : [[target.lng - tPad, target.lat - tPad], [target.lng + tPad, target.lat + tPad]];
      map.fitBounds(bounds, { padding: 60, duration: 600 });

      // Draw user dot if GPS already fired before map loaded
      if (userPosRef.current) applyUserPos(map, userPosRef.current);
    });

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      posMarkerRef.current?.remove();
      try { map.remove(); } catch {}
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyUserPos(map, pos) {
    if (!map || !map.isStyleLoaded()) return;
    const { lat, lng } = pos;
    if (posMarkerRef.current) {
      posMarkerRef.current.setLngLat([lng, lat]);
    } else {
      const el = document.createElement("div");
      el.style.cssText = `
        width:18px;height:18px;border-radius:50%;
        background:#3b82f6;border:3px solid #fff;
        box-shadow:0 0 0 5px rgba(59,130,246,.25),0 2px 8px rgba(0,0,0,.3);
      `;
      posMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([lng, lat])
        .addTo(map);
    }
    // Update dashed line user → target
    if (map.getSource("nm-line")) {
      map.getSource("nm-line").setData({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [[lng, lat], [target.lng, target.lat]] },
      });
    }
  }

  // Start GPS watch
  useEffect(() => {
    if (!navigator.geolocation) { setPosErr(true); return; }
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const p = { lat, lng };
        userPosRef.current = p;
        setUserPos(p);
        applyUserPos(mapRef.current, p);
      },
      () => setPosErr(true),
      { enableHighAccuracy: true, maximumAge: 4000, timeout: 15000 }
    );
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dist = userPos
    ? haversineM(userPos.lat, userPos.lng, target.lat, target.lng)
    : null;

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}`;

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 8000,
      display: "flex", flexDirection: "column",
      background: "var(--bg,#f8fafc)",
    }}>
      {/* Top bar */}
      <div style={{
        background: "var(--panel,#fff)",
        borderBottom: "1px solid var(--border,#e5e7eb)",
        padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10,
        flexShrink: 0,
      }}>
        <button type="button" onClick={onClose} style={{
          background: "transparent",
          border: "1px solid var(--border,#e5e7eb)",
          borderRadius: 8, padding: "7px 13px",
          cursor: "pointer", fontSize: 13, fontWeight: 700,
          color: "var(--text,#111)", flexShrink: 0,
        }}>← Назад</button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 800, fontSize: 14, color: "var(--text,#111)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            📍 {target.label}
          </div>
          <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)", fontFamily: "monospace" }}>
            {target.lat.toFixed(5)}, {target.lng.toFixed(5)}
          </div>
        </div>

        {dist !== null && (
          <div style={{
            background: "rgba(59,130,246,.1)",
            border: "1px solid rgba(59,130,246,.3)",
            borderRadius: 10, padding: "5px 12px",
            textAlign: "center", flexShrink: 0,
          }}>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#2563eb" }}>{fmtDist(dist)}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: ".04em" }}>від вас</div>
          </div>
        )}

        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flexShrink: 0,
            background: "#2563eb", color: "#fff",
            borderRadius: 9, padding: "8px 13px",
            fontSize: 12, fontWeight: 700, textDecoration: "none",
            display: "flex", alignItems: "center", gap: 5,
            boxShadow: "0 2px 8px rgba(37,99,235,.35)",
          }}
        >🧭 Маршрут</a>
      </div>

      {/* GPS status banner */}
      {!userPos && (
        <div style={{
          background: posErr ? "rgba(239,68,68,.07)" : "rgba(59,130,246,.06)",
          borderBottom: "1px solid " + (posErr ? "rgba(239,68,68,.2)" : "rgba(59,130,246,.15)"),
          padding: "7px 14px", fontSize: 11, flexShrink: 0,
          color: posErr ? "#dc2626" : "#2563eb", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          {posErr
            ? "⚠️ Неможливо визначити позицію. Перевірте дозвіл на геолокацію в браузері."
            : "📡 Визначаю вашу позицію…"}
        </div>
      )}

      {/* Map */}
      <div ref={containerRef} style={{ flex: 1, minHeight: 0 }} />

      {/* Bottom legend */}
      <div style={{
        background: "var(--panel,#fff)",
        borderTop: "1px solid var(--border,#e5e7eb)",
        padding: "8px 14px", flexShrink: 0,
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: target.color || "#ef4444", border: "2px solid #fff", boxShadow: `0 0 0 2px ${target.color || "#ef4444"}44` }} />
          <span style={{ fontSize: 11, color: "var(--muted,#6b7280)" }}>
            Місце події{target.radius ? ` · радіус ${target.radius}м` : ""}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#3b82f6", border: "2px solid #fff", boxShadow: "0 0 0 2px rgba(59,130,246,.3)" }} />
          <span style={{ fontSize: 11, color: "var(--muted,#6b7280)" }}>{userPos ? "Ваша позиція" : "Очікую позицію…"}</span>
        </div>
        {userPos && (
          <div style={{ marginLeft: "auto", fontSize: 10, color: "var(--muted,#9ca3af)", fontFamily: "monospace" }}>
            {userPos.lat.toFixed(5)}, {userPos.lng.toFixed(5)}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── ConfirmDialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ isOpen, onConfirm, onCancel, message }) {
  if (!isOpen) return null;
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6000,
    }} onClick={onCancel}>
      <div style={{
        background: "var(--modal-bg,var(--panel,#fff))",
        border: "1px solid var(--border,#e5e7eb)",
        borderRadius: 16, padding: "24px 20px", width: "min(340px, calc(100vw - 32px))",
        textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)",
        color: "var(--text,#111)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>🗑️</div>
        <p style={{ margin: "0 0 20px", fontSize: 14, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={onConfirm} style={{
            background: "#dc2626", color: "#fff", border: "none",
            padding: "9px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 700,
            minHeight: 44, flex: 1,
          }}>Так, видалити</button>
          <button onClick={onCancel} style={{
            background: "transparent", color: "var(--muted,#6b7280)",
            border: "1px solid var(--border,#e5e7eb)", padding: "9px 20px",
            borderRadius: 9, cursor: "pointer", fontSize: 14,
            minHeight: 44, flex: 1,
          }}>Скасувати</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── PhotoViewer ──────────────────────────────────────────────────────────────
function PhotoViewer({ photos, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex ?? 0);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape")       { onClose(); }
      else if (e.key === "ArrowRight") setIdx(i => Math.min(i + 1, photos.length - 1));
      else if (e.key === "ArrowLeft")  setIdx(i => Math.max(i - 1, 0));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, photos.length]);

  if (!photos.length) return null;

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.88)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 16, right: 16,
          background: "rgba(255,255,255,.15)", color: "#fff",
          border: "none", borderRadius: "50%",
          width: 40, height: 40, fontSize: 18,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700,
        }}
      >✕</button>

      {photos.length > 1 && (
        <div style={{
          position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)",
          color: "#fff", fontSize: 13, fontWeight: 600, opacity: 0.75,
          background: "rgba(0,0,0,.4)", padding: "3px 12px", borderRadius: 20,
        }}>
          {idx + 1} / {photos.length}
        </div>
      )}

      <div
        style={{ position: "relative", maxWidth: "90vw", maxHeight: "80vh" }}
        onClick={e => e.stopPropagation()}
      >
        <img
          src={photos[idx]} alt=""
          style={{
            maxWidth: "90vw", maxHeight: "80vh",
            objectFit: "contain", borderRadius: 10,
            boxShadow: "0 8px 40px rgba(0,0,0,.6)", display: "block",
          }}
        />

        {idx > 0 && (
          <button
            onClick={() => setIdx(i => i - 1)}
            style={{
              position: "absolute", left: -52, top: "50%", transform: "translateY(-50%)",
              background: "rgba(255,255,255,.18)", color: "#fff",
              border: "none", borderRadius: "50%",
              width: 44, height: 44, fontSize: 22,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >‹</button>
        )}

        {idx < photos.length - 1 && (
          <button
            onClick={() => setIdx(i => i + 1)}
            style={{
              position: "absolute", right: -52, top: "50%", transform: "translateY(-50%)",
              background: "rgba(255,255,255,.18)", color: "#fff",
              border: "none", borderRadius: "50%",
              width: 44, height: 44, fontSize: 22,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >›</button>
        )}
      </div>

      {photos.length > 1 && (
        <div
          style={{ display: "flex", gap: 8, marginTop: 16 }}
          onClick={e => e.stopPropagation()}
        >
          {photos.map((url, j) => (
            <img
              key={j} src={url} alt=""
              onClick={() => setIdx(j)}
              style={{
                width: 56, height: 44, objectFit: "cover", borderRadius: 6,
                border: j === idx ? "2px solid #fff" : "2px solid transparent",
                opacity: j === idx ? 1 : 0.5,
                cursor: "pointer", transition: "all .15s",
              }}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}

// ─── ГОЛОВНИЙ КОМПОНЕНТ ───────────────────────────────────────────────────────
export default function FieldEventPanel({ user, fieldId, onToast, fieldGeometry, previousFieldGeometry, activeSeasonId, onGoToOverview }) {
  const [events,       setEvents]       = useState([]);
  const [seasons,      setSeasons]      = useState([]);
  // fieldGeometry — проп з FieldDetail (вже нормалізований), fallback — власний фетч
  const [fetchedGeo,   setFetchedGeo]   = useState(null);
  const fieldGeoJson = fieldGeometry ?? fetchedGeo;
  const [loading,      setLoading]      = useState(true);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [confirm,      setConfirm]      = useState({ open: false, eventId: null });
  const [saving,       setSaving]       = useState(false);
  const [selectedPointKey, setSelectedPointKey] = useState(null);
  const [photoViewer,  setPhotoViewer]  = useState(null); // { photos: string[], index: number }
  const [navTarget,    setNavTarget]    = useState(null); // { lat, lng, label, color }

  const [name,           setName]          = useState("");
  const [desc,           setDesc]          = useState("");
  const [seasonId,       setSeasonId]      = useState("");
  const [eventPointType, setEventPointType] = useState("OTHER");
  const [fieldEventType, setFieldEventType] = useState("NOTE");
  const [draftPoints,    setDraftPoints]   = useState([]);
  const [inputMode,      setInputMode]     = useState("type"); // "type" | "map"
  const [formPhotos,     setFormPhotos]    = useState([]);

  // Filter states
  const [filterSeason, setFilterSeason] = useState("active"); // "active" | "all" | "<id>"
  const [filterType,   setFilterType]   = useState("all");    // "all" | WEED | PEST | DISEASE | OTHER
  const [sortOrder,    setSortOrder]    = useState("desc");   // "desc" | "asc"

  // Коли активний сезон змінюється ззовні — оновлюємо вибір у формі
  useEffect(() => {
    if (activeSeasonId) {
      setSeasonId(String(activeSeasonId));
    }
  }, [activeSeasonId]);

  const visibleEvents = useMemo(() => {
    let list = [...events];

    // Season filter
    if (filterSeason === "active") {
      if (activeSeasonId) list = list.filter(e => String(e.seasonId) === String(activeSeasonId));
    } else if (filterSeason !== "all") {
      list = list.filter(e => String(e.seasonId) === filterSeason);
    }

    // Type filter
    if (filterType !== "all") {
      list = list.filter(e =>
        (e.pointType === filterType) ||
        e.points?.some(p => p.pointType === filterType)
      );
    }

    // Sort by id (proxy for creation order)
    list.sort((a, b) => sortOrder === "desc" ? b.id - a.id : a.id - b.id);

    return list;
  }, [events, filterSeason, filterType, sortOrder, activeSeasonId]);

  // ── Завантаження ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return; // чекаємо поки parent передасть user
    let alive = true;
    setLoading(true);
    Promise.all([
      fetchField().then(geo   => { if (alive) setFetchedGeo(geo); }),
      fetchEvents().then(list => { if (alive) setEvents(list); }),
      fetchSeasons().then(list => { if (alive) setSeasons(list); }),
    ]).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, user]);

  async function fetchField() {
    try {
      const res = await apiFetch(user, `/api/fields/${fieldId}`);
      if (!res.ok) return null;
      const raw = await res.json();

      // Однаковий набір ключів як у normalizeFieldFromApi в FieldDetail
      function tryParse(v) {
        if (!v) return null;
        if (typeof v === "object") return v;
        try { return JSON.parse(v); } catch { return null; }
      }

      return tryParse(raw.geometry)
          ?? tryParse(raw.geometryJson)
          ?? tryParse(raw.contour)
          ?? tryParse(raw.shape)
          ?? null;
    } catch { return null; }
  }

  async function fetchSeasons() {
    try {
      const res  = await apiFetch(user, `/api/fields/${fieldId}/seasons`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : (data?.items ?? []);
    } catch { return []; }
  }

  function evStorageKey() { return `pev_${fieldId}`; }
  function evLoadCache()  {
    try { return JSON.parse(localStorage.getItem(evStorageKey()) || "[]"); } catch { return []; }
  }
  function evSaveCache(list) {
    try { localStorage.setItem(evStorageKey(), JSON.stringify(list)); } catch {}
  }

  async function fetchEvents() {
    try {
      const res  = await apiFetch(user, `/api/fields/${fieldId}/problem-events`);
      if (!res.ok) return evLoadCache();           // GET немає на бекенді → беремо з кешу
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.items ?? data?.content ?? []);
      if (list.length > 0) evSaveCache(list);      // синхронізуємо кеш якщо бек повернув дані
      return list;
    } catch { return evLoadCache(); }
  }


  // ── Точки ───────────────────────────────────────────────────────────────────
  const handleMapClick = useCallback((latLng) => {
    setDraftPoints(prev => [
      ...prev,
      { lat: latLng.lat, lng: latLng.lng, pointType: "OTHER", hasRadius: false, radius: 10, photos: [], hasNote: false, note: "" },
    ]);
  }, []);

  function updatePoint(idx, field, value) {
    setDraftPoints(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  }
  function removePoint(idx) {
    setDraftPoints(prev => prev.filter((_, i) => i !== idx));
  }

  // ── Зберегти ────────────────────────────────────────────────────────────────
  async function save(e) {
    e.preventDefault();
    if (!name.trim()) { showToast("⚠️ Вкажіть назву події"); return; }
    if (!seasonId)    { showToast("⚠️ Оберіть сезон"); return; }

    setSaving(true);
    try {
      const body = {
        name:           name.trim(),
        describe:       desc.trim(),
        seasonId:       Number(seasonId),
        pointType:      eventPointType,
        fieldEventType: fieldEventType,
        photoUrls: inputMode === "type" ? formPhotos.map(ph => ph.url).filter(Boolean) : [],
        points: inputMode === "map" ? draftPoints.map(p => ({
          latitude:  p.lat,
          longitude: p.lng,
          pointType: p.pointType,
          radius:    p.hasRadius ? p.radius : null,
          note:      p.hasNote && p.note?.trim() ? p.note.trim() : null,
          photoUrls: (p.photos ?? []).map(ph => ph.url).filter(Boolean),
        })) : [],
      };

      const res = await apiFetch(user, `/api/fields/${fieldId}/problem-events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        showToast("❌ " + (txt || `Помилка ${res.status}`));
        return;
      }

      let newEvent = null;
      try { newEvent = await res.json(); } catch {}

      const displayEvent = {
        id:             newEvent?.id ?? Date.now(),
        name:           name.trim(),
        describe:       desc.trim(),
        seasonId:       Number(seasonId),
        pointType:      eventPointType,
        fieldEventType: fieldEventType,
        createdAt:      newEvent?.createdAt ?? new Date().toISOString(),
        points: draftPoints.map((p, i) => ({
          id:        newEvent?.points?.[i]?.id,
          latitude:  p.lat,
          longitude: p.lng,
          pointType: p.pointType,
          radius:    p.hasRadius ? p.radius : null,
          note:      p.hasNote && p.note?.trim() ? p.note.trim() : null,
          photoUrls: (p.photos ?? []).map(ph => ph.url).filter(Boolean),
        })),
      };
      setEvents(prev => {
        const next = [displayEvent, ...prev];
        evSaveCache(next);
        return next;
      });

      resetForm();
      setModalOpen(false);
      if (onToast) onToast("ok", "Подію збережено ✅");
      else showToast("✅ Подію збережено");
    } catch (err) {
      console.error(err);
      showToast("❌ Помилка збереження");
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setName(""); setDesc(""); setDraftPoints([]);
    setEventPointType("OTHER"); setFieldEventType("NOTE");
    setSeasonId(seasons.length ? String(seasons[0].id) : "");
    setInputMode("type"); setFormPhotos([]);
  }

  function openModal() { resetForm(); setModalOpen(true); }

  // ── Видалити точку ──────────────────────────────────────────────────────────
  async function handleDeletePoint(eventId, pointId) {
    try {
      await apiFetch(user, `/api/fields/${fieldId}/problem-events/${eventId}/points/${pointId}`, { method: "DELETE" });
      setEvents(prev => {
        const next = prev.map(ev =>
          ev.id === eventId
            ? { ...ev, points: (ev.points || []).filter(p => p.id !== pointId) }
            : ev
        );
        evSaveCache(next);
        return next;
      });
    } catch {
      showToast("❌ Помилка видалення точки");
    }
  }

  // ── Видалити подію ───────────────────────────────────────────────────────────
  async function confirmDelete() {
    try {
      await apiFetch(user, `/api/fields/${fieldId}/problem-events/${confirm.eventId}`, { method: "DELETE" });
      setEvents(prev => {
        const next = prev.filter(e => e.id !== confirm.eventId);
        evSaveCache(next);
        return next;
      });
      if (onToast) onToast("ok", "Подію видалено ✅");
      else showToast("🗑️ Видалено");
    } catch {
      showToast("❌ Помилка видалення");
    } finally {
      setConfirm({ open: false, eventId: null });
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  const totalPoints = visibleEvents.reduce((acc, e) => acc + (e.points?.length || 0), 0);

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <style>{`@keyframes evSpin{to{transform:rotate(360deg)}}`}</style>

      {/* Шапка */}
      <div className="fd-sectionHead">
        <div>
          <div className="fd-sectionTitle">📋 Проблемні події</div>
          <div className="fd-sectionSub">
            {loading
              ? "Завантаження..."
              : `${visibleEvents.length} ${plural(visibleEvents.length,"подія","події","подій")} · ${totalPoints} ${plural(totalPoints,"точка","точки","точок")}`
            }
          </div>
        </div>
        <button className="btn primary" onClick={openModal} disabled={loading} type="button" style={{ minHeight: 44 }}>
          + Нова подія
        </button>
      </div>

      {/* Фільтри */}
      {!loading && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
          {/* Сезон */}
          <select
            value={filterSeason}
            onChange={e => setFilterSeason(e.target.value)}
            style={{
              fontSize: 14, padding: "10px 12px", borderRadius: 10, minHeight: 44,
              border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)",
              cursor: "pointer",
            }}
          >
            <option value="active">Поточний сезон</option>
            <option value="all">Всі сезони</option>
            {seasons.map(s => (
              <option key={s.id} value={String(s.id)}>
                {s.name || s.year || `Сезон ${s.id}`}
              </option>
            ))}
          </select>

          {/* Тип точки */}
          <div style={{ display: "flex", gap: 4 }}>
            {[{ value: "all", label: "Всі", icon: "📋" }, ...POINT_TYPES].map(pt => (
              <button
                key={pt.value}
                type="button"
                onClick={() => setFilterType(pt.value)}
                style={{
                  fontSize: 12, padding: "4px 10px", borderRadius: 20,
                  border: "1px solid var(--border)",
                  background: filterType === pt.value ? "var(--accent, #16a34a)" : "var(--card)",
                  color: filterType === pt.value ? "#fff" : "var(--text)",
                  cursor: "pointer", fontWeight: filterType === pt.value ? 700 : 400,
                  transition: "background .15s",
                }}
              >
                {pt.icon} {pt.label || "Всі"}
              </button>
            ))}
          </div>

          {/* Сортування */}
          <button
            type="button"
            onClick={() => setSortOrder(o => o === "desc" ? "asc" : "desc")}
            style={{
              fontSize: 12, padding: "4px 10px", borderRadius: 8,
              border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)",
              cursor: "pointer", marginLeft: "auto",
            }}
          >
            {sortOrder === "desc" ? "↓ Новіші" : "↑ Старіші"}
          </button>
        </div>
      )}

      {/* Лоадер */}
      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)" }}>
          <div style={{
            width: 32, height: 32, margin: "0 auto",
            border: "3px solid var(--border)", borderTop: "3px solid #16a34a",
            borderRadius: "50%", animation: "evSpin .75s linear infinite",
          }} />
          <p style={{ marginTop: 12, fontSize: 13 }}>Завантаження...</p>
        </div>
      )}

      {/* Карта огляду */}
      {!loading && fieldGeoJson && visibleEvents.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{
            fontSize: 12, color: "var(--muted)", fontWeight: 700, marginBottom: 6,
            textTransform: "uppercase", letterSpacing: ".04em",
          }}>Всі точки на полі</div>
          <ViewMap fieldGeoJson={fieldGeoJson} previousGeoJson={previousFieldGeometry} events={visibleEvents} selectedKey={selectedPointKey} onOpenPhotos={(photos, i) => setPhotoViewer({ photos, index: i })} onSelectPoint={setSelectedPointKey} onNavigate={setNavTarget} />
          {previousFieldGeometry && (
            <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 18, height: 3, background: "#22c55e", borderRadius: 2, display: "inline-block" }} />
                Поточний контур
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 18, height: 3, background: "#f97316", borderRadius: 2, display: "inline-block", opacity: 0.8 }} />
                Попередній контур
              </span>
            </div>
          )}
        </div>
      )}

      {/* Порожній стан */}
      {!loading && visibleEvents.length === 0 && (
        <div style={{
          textAlign: "center", padding: "56px 20px",
          border: "1px dashed var(--border)", borderRadius: 14, marginTop: 16,
        }}>
          <div style={{ fontSize: 44 }}>🌾</div>
          <p style={{ margin: "8px 0 4px", fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
            Немає жодної події
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>
            Натисніть «Нова подія» щоб зафіксувати проблему на полі
          </p>
          <button
            className="btn primary" onClick={openModal} type="button"
            style={{ marginTop: 16, display: "inline-flex" }}
          >+ Додати першу подію</button>
        </div>
      )}

      {/* Список подій */}
      {!loading && visibleEvents.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {visibleEvents.map(ev => (
            <EventCard
              key={ev.id} event={ev}
              fieldGeoJson={fieldGeoJson}
              onDelete={() => setConfirm({ open: true, eventId: ev.id })}
              onDeletePoint={handleDeletePoint}
              selectedPointKey={selectedPointKey}
              onSelectPoint={setSelectedPointKey}
              onOpenPhotos={(photos, i) => setPhotoViewer({ photos, index: i })}
              onNavigate={setNavTarget}
            />
          ))}
        </div>
      )}

      {/* ─── Модалка ─────────────────────────────────────────────────────────── */}
      {modalOpen && createPortal(
        <div className="fd-modal" onClick={() => setModalOpen(false)}>
          <div
            className="fd-modalCard"
            style={{ width: "min(620px, 95vw)" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Заголовок */}
            <div className="fd-modalHead">
              <div>
                <div className="fd-modalTitle">➕ Нова подія поля</div>
              </div>
              <button className="btn" type="button" onClick={() => setModalOpen(false)}>✕</button>
            </div>

            <form className="fd-modalBody" onSubmit={save}>
              {/* Назва */}
              <div className="fd-fg" style={{ marginBottom: 12 }}>
                <div className="fd-lbl">
                  Назва події <span style={{ color: "#ef4444" }}>*</span>
                </div>
                <input
                  className="fd-inputXL"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Напр.: Осередок бур'яну, Пошкодження посівів..."
                  maxLength={20}
                  autoFocus
                />
                <div style={{
                  fontSize: 10, textAlign: "right", marginTop: 2,
                  color: name.length > 16 ? "#ea580c" : "var(--muted)",
                }}>
                  {name.length}/20
                </div>
              </div>

              {/* Сезон */}
              <div className="fd-fg" style={{ marginBottom: 12 }}>
                <div className="fd-lbl">
                  Сезон <span style={{ color: "#ef4444" }}>*</span>
                </div>
                {seasons.length === 0 ? (
                  <div style={{
                    fontSize: 12, color: "var(--muted)", padding: "10px 12px",
                    border: "1px solid var(--border,#e5e7eb)", borderRadius: 9,
                    background: "var(--surface-soft,#f9fafb)",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  }}>
                    <span>Сезони не знайдено. Спочатку створіть сезон для поля.</span>
                    {onGoToOverview && (
                      <button
                        type="button"
                        onClick={onGoToOverview}
                        style={{
                          flexShrink: 0, fontSize: 11, fontWeight: 700, cursor: "pointer",
                          background: "var(--primary,#22c55e)", color: "#fff",
                          border: "none", borderRadius: 7, padding: "4px 10px",
                        }}
                      >
                        → Огляд
                      </button>
                    )}
                  </div>
                ) : (
                  <select
                    className="fd-inputXL"
                    value={seasonId}
                    onChange={e => setSeasonId(e.target.value)}
                    style={{ cursor: "pointer" }}
                  >
                    <option value="">— оберіть сезон —</option>
                    {seasons.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name ?? s.year ?? `Сезон ${s.id}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Перемикач режиму */}
              <div className="fd-fg" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setInputMode("type"); setDraftPoints([]); }}
                    style={{
                      flex: 1, padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                      border: inputMode === "type" ? "2px solid #16a34a" : "2px solid var(--border,#e5e7eb)",
                      background: inputMode === "type" ? "rgba(22,163,74,.10)" : "var(--panel,#fff)",
                      color: inputMode === "type" ? "#16a34a" : "var(--muted,#6b7280)",
                      fontWeight: 800, fontSize: 13, transition: "all .12s",
                    }}
                  >
                    📋 Тип події
                  </button>
                  <button
                    type="button"
                    onClick={() => { setInputMode("map"); setFormPhotos([]); }}
                    style={{
                      flex: 1, padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                      border: inputMode === "map" ? "2px solid #3b82f6" : "2px solid var(--border,#e5e7eb)",
                      background: inputMode === "map" ? "rgba(59,130,246,.10)" : "var(--panel,#fff)",
                      color: inputMode === "map" ? "#3b82f6" : "var(--muted,#6b7280)",
                      fontWeight: 800, fontSize: 13, transition: "all .12s",
                    }}
                  >
                    📍 Точки на карті
                  </button>
                </div>
              </div>

              {/* === РЕЖИМ: ТИП ПОДІЇ === */}
              {inputMode === "type" && (<>
                <div className="fd-fg" style={{ marginBottom: 12 }}>
                  <div className="fd-lbl">Тип події</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
                    {FIELD_EVENT_TYPES.map(fet => (
                      <button
                        key={fet.value} type="button"
                        onClick={() => setFieldEventType(fet.value)}
                        style={{
                          padding: "5px 11px", borderRadius: 8,
                          fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all .12s",
                          border: fieldEventType === fet.value ? "2px solid #16a34a" : "2px solid transparent",
                          background: fieldEventType === fet.value ? "rgba(22,163,74,.12)" : "var(--panel,#fff)",
                          color: fieldEventType === fet.value ? "#16a34a" : "var(--muted,#6b7280)",
                        }}
                      >
                        {fet.icon} {fet.label}
                      </button>
                    ))}
                  </div>
                </div>


                <div className="fd-fg" style={{ marginBottom: 12 }}>
                  <div className="fd-lbl">Опис</div>
                  <textarea
                    className="fd-textareaXL"
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    placeholder="Додаткова інформація: ступінь ураження, рекомендації..."
                    maxLength={200}
                    rows={3}
                    style={{ minHeight: 72, resize: "vertical" }}
                  />
                  <div style={{ fontSize: 10, textAlign: "right", marginTop: 2, color: desc.length > 180 ? "#ea580c" : "var(--muted)" }}>
                    {desc.length}/200
                  </div>
                </div>

                <div className="fd-fg" style={{ marginBottom: 14 }}>
                  <PhotoSection photos={formPhotos} onChange={setFormPhotos} />
                </div>
              </>)}

              {/* === РЕЖИМ: ТОЧКИ НА КАРТІ === */}
              {inputMode === "map" && (<>
                <div className="fd-fg" style={{ marginBottom: 12 }}>
                  <div className="fd-lbl">Опис</div>
                  <textarea
                    className="fd-textareaXL"
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    placeholder="Додаткова інформація..."
                    maxLength={200}
                    rows={3}
                    style={{ minHeight: 72, resize: "vertical" }}
                  />
                  <div style={{ fontSize: 10, textAlign: "right", marginTop: 2, color: desc.length > 180 ? "#ea580c" : "var(--muted)" }}>
                    {desc.length}/200
                  </div>
                </div>

                <div className="fd-fg" style={{ marginBottom: 14 }}>
                  <div className="fd-lbl" style={{ marginBottom: 6 }}>
                    Точки на карті{" "}
                    <span style={{
                      background: draftPoints.length ? "rgba(59,130,246,.12)" : "var(--surface-soft,#f5f5f5)",
                      color: draftPoints.length ? "#3b82f6" : "var(--muted)",
                      border: `1px solid ${draftPoints.length ? "rgba(59,130,246,.3)" : "var(--border,#e5e7eb)"}`,
                      padding: "1px 8px", borderRadius: 20, fontSize: 10, fontWeight: 800,
                    }}>
                      {draftPoints.length}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
                    Натисніть на карту або «Моя позиція» щоб додати точку
                  </div>
                  <EditMap
                    fieldGeoJson={fieldGeoJson}
                    draftPoints={draftPoints}
                    onAddPoint={handleMapClick}
                  />
                </div>

                {draftPoints.length > 0 && (
                  <div className="fd-fg" style={{ marginBottom: 14 }}>
                    <div className="fd-lbl" style={{ marginBottom: 8 }}>
                      Налаштування точок
                      <button
                        type="button" onClick={() => setDraftPoints([])}
                        style={{
                          marginLeft: 10, fontSize: 10, padding: "2px 9px",
                          border: "1px solid #fca5a5", color: "#ef4444",
                          background: "rgba(239,68,68,.06)",
                          borderRadius: 6, cursor: "pointer", fontWeight: 700,
                        }}
                      >Очистити всі</button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {draftPoints.map((pt, i) => (
                        <PointRow key={i} point={pt} index={i} onChange={updatePoint} onRemove={removePoint} />
                      ))}
                    </div>
                  </div>
                )}

                {draftPoints.length === 0 && (
                  <div style={{
                    textAlign: "center", padding: "12px", marginBottom: 14,
                    border: "1px dashed var(--border,#e5e7eb)", borderRadius: 10,
                    fontSize: 12, color: "var(--muted,#9ca3af)",
                  }}>
                    👆 Натисніть на карту щоб додати точку
                  </div>
                )}
              </>)}

              {/* Кнопки */}
              <div className="fd-stickyActions" style={{ gap: 10 }}>
                <button className="btn" type="button" onClick={() => setModalOpen(false)} disabled={saving} style={{ minHeight: 50 }}>
                  Скасувати
                </button>
                <button className="btn primary" type="submit" disabled={saving} style={{ minHeight: 50 }}>
                  {saving ? "Зберігаю..." : "💾 Зберегти подію"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ConfirmDialog
        isOpen={confirm.open}
        onConfirm={confirmDelete}
        onCancel={() => setConfirm({ open: false, eventId: null })}
        message="Видалити цю подію разом із усіма точками?"
      />

      {photoViewer && (
        <PhotoViewer
          photos={photoViewer.photos}
          startIndex={photoViewer.index}
          onClose={() => setPhotoViewer(null)}
        />
      )}

      {navTarget && (
        <NavigateMap
          fieldGeoJson={fieldGeoJson}
          target={navTarget}
          onClose={() => setNavTarget(null)}
        />
      )}
    </div>
  );
}
